import {
    ALPHA_2_ISO_3166,
    APPLICANT_CREDIT_RATING_REQUESTED_STATUSES,
    EMPLOYMENT_INDUSTRY,
    EMPLOYMENT_LIMITATIONS,
    EMPLOYMENT_TYPES,
    LIVING_SITUATIONS,
    PERSONAL_STATUS,
    SALUTATIONS,
    TAX_CLASSIFICATIONS,
} from 'cpool-constants';
import { Applicant } from 'cpool-schemas';
import { z } from 'zod';

import { censorApplicant } from '../../../utils/censor.utils';
import type { EventSchema } from '../../../utils/EventSchema';
import { _Event } from '../../_Event';

export class LoanRequest_ApplicantIndexed extends _Event {
    constructor(payload: EventSchema<LoanRequest_ApplicantIndexed>, deprecated = false) {
        super(payload, deprecated);
    }

    getSchema() {
        return z.object({
            applicant: Applicant,
            isSecondApplicant: z.boolean(),
        });
    }

    getCensoredSchema() {
        return this.getSchema().transform((data) => ({
            ...data,
            applicant: censorApplicant(data.applicant),
        }));
    }

    getCensoredSchemaApplicant2Only(applicant2Id: string) {
        return this.getSchema().transform((data) => ({
            ...data,
            applicant: data.applicant.id === applicant2Id ? censorApplicant(data.applicant) : data.applicant,
        }));
    }

    static example(): EventSchema<LoanRequest_ApplicantIndexed> {
        return {
            applicant: {
                advertisementConsentAgreed: false,
                beschaeftigung: {
                    angestellter: {
                        beschaeftigungsverhaeltnis: {
                            arbeitgeber: {
                                anschrift: {
                                    hausnummer: '1',
                                    land: ALPHA_2_ISO_3166.DE,
                                    ort: 'Musterstadt',
                                    plz: '12345',
                                    strasse: 'Musterstrasse',
                                },
                                branche: EMPLOYMENT_INDUSTRY.HANDEL,
                                name: 'Muster AG',
                            },
                            befristetBis: new Date('2023-07-18'),
                            befristung: EMPLOYMENT_LIMITATIONS.BEFRISTET,
                            berufsbezeichnung: 'Softwareentwickler',
                            beschaeftigtSeit: new Date('2023-07-17'),
                            inProbezeit: true,
                            inProbezeitBis: new Date('2023-07-18'),
                            kumulierterJahreswert: 36000,
                            nettoeinkommenMonatlich: 3000,
                            nettoeinkommenVorletzterMonat: 2000,
                            nettoeinkommenVorVorletzterMonat: 1000,
                        },
                    },
                    beschaeftigungsart: EMPLOYMENT_TYPES.ANGESTELLTER,
                    steuerklasse: TAX_CLASSIFICATIONS.CLASS_1,
                },
                creditRatingRequestedStatus: APPLICANT_CREDIT_RATING_REQUESTED_STATUSES.NOT_REQUESTED,
                dataProtectionAgreed: false,
                emailVerified: false,
                id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
                legitimation: {
                    telephone: [],
                },
                personendaten: {
                    anrede: SALUTATIONS.HERR,
                    email: 'something@example.com',
                    familienstand: PERSONAL_STATUS.LEDIG,
                    geburtsdatum: new Date('2023-07-17'),
                    geburtsland: ALPHA_2_ISO_3166.AD,
                    geburtsname: 'John',
                    geburtsort: 'Hameln',
                    nachname: 'Doe',
                    staatsangehoerigkeit: ALPHA_2_ISO_3166.AD,
                    telefonPrivat: '+4917675879507',
                    vorname: 'John',
                },
                wohnsituation: {
                    anschrift: {
                        hausnummer: '837',
                        land: ALPHA_2_ISO_3166.AD,
                        ort: 'Hameln',
                        plz: '18059',
                        strasse: 'Dessauer',
                        wohnhaftSeit: new Date('2023-07-17'),
                    },
                    anzahlPersonenImHaushalt: 500,
                    anzahlPkw: null,
                    gemeinsamerHaushalt: false,
                    vorherigeAnschrift: {
                        hausnummer: '837',
                        land: ALPHA_2_ISO_3166.AD,
                        ort: 'Hameln',
                        plz: '18059',
                        strasse: 'Dessauer',
                        wohnhaftSeit: new Date('2023-07-17'),
                    },
                    wohnart: LIVING_SITUATIONS.ZUR_MIETE,
                    wohnflaeche: 1000,
                },
            },
            isSecondApplicant: false,
        };
    }

    static exampleCensored(): z.infer<ReturnType<LoanRequest_ApplicantIndexed['getCensoredSchema']>> {
        const censoredExample = LoanRequest_ApplicantIndexed.example();
        censoredExample.applicant = censorApplicant(censoredExample.applicant);
        return censoredExample;
    }

    static exampleCensoredApplicant2Only(
        applicant2Id: string,
    ): z.infer<ReturnType<LoanRequest_ApplicantIndexed['getCensoredSchemaApplicant2Only']>> {
        const example = LoanRequest_ApplicantIndexed.example();
        return {
            ...example,
            applicant: example.applicant.id === applicant2Id ? censorApplicant(example.applicant) : example.applicant,
        };
    }
}

// INFO your SCHEMA
//     static get SCHEME() {
//         return yup.object({
//             applicant: LoanRequest_ApplicantCreated.SCHEME,
//             isSecondApplicant: yup.boolean().required(),
//         });
//     }
